'use client';

import { StatusBadgeNew } from '@c/dashboard/StatusBadgeNew';
import { useQuery } from '@tanstack/react-query';
import ProductPriceRange from '@ui/ProductPriceRange';
import SafeImage from '@ui/SafeImage';
import { getProductsByIds } from '@util/firestore/products';
import type { OfferDocument } from 'models/offer';
import { getOrderStatus, type OrderDocument } from 'models/order';
import type { ProductDocument } from 'models/product';
import type { UserDocument } from 'models/user';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { ProductCategoryDetails } from './ChatPreviewSidebar';
import Chip from '@ui/Chip';
import { getOfferStatusBadge, offerStateToText } from '@util/index';
import { SoldOverlay } from '@c/dashboard/pages/ListingsPage';

const getChipColor = (status: string) => {
  switch (status) {
    case 'accepted':
      return 'offer-accepted';
    case 'declined':
      return 'offer-declined';
    case 'pending':
      return 'offer-pending';
    case 'expired':
      return 'offer-expired';
    default:
      return 'gray';
  }
};

export type MobileChatPreviewProps = {
  product: ProductDocument | null | undefined;
  offer: OfferDocument | null | undefined;
  order: OrderDocument | null | undefined;
  userDoc: UserDocument | null | undefined;
  isOutgoingOffer?: boolean;
  isLoading: boolean;
  action?: ReactNode;
};

export default function MobileChatPreview({
  product,
  offer,
  order,
  userDoc,
  isOutgoingOffer = false,
  isLoading,
  action,
}: MobileChatPreviewProps) {
  const { data: products } = useQuery({
    queryKey: ['products', order?.product_ids],
    queryFn: () => getProductsByIds(order?.product_ids ?? []),
    enabled: !!order?.product_ids,
  });

  if (!product && !offer && !order) return null;

  const PreviewContent = () => {
    if (offer && product) {
      const selectedVariations = {
        size: offer?.variation?.letter || offer?.variation?.number,
        color: offer?.variation?.color,
      };

      if (isLoading) {
        return (
          <div className="flex items-center gap-6">
            <div className="aspect-square h-[6rem] w-[6rem] animate-pulse rounded-2xl bg-zinc-200" />

            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <div className="h-[2.5rem] w-[6.4rem] animate-pulse rounded-xl bg-zinc-200" />
                <div className="h-[2.5rem] w-[8rem] animate-pulse rounded-xl bg-zinc-200" />
              </div>

              <div className="flex flex-col items-start gap-1">
                <div className="h-[1.8rem] w-[35rem] animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.4rem] w-24 animate-pulse rounded bg-zinc-200" />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-6">
          <Link href={`/product/${product.slug}`} target="_blank">
            <div className="relative">
              <SafeImage
                src={product?.thumbnail}
                alt={product?.title ?? ''}
                width={400}
                height={400}
                className="aspect-square h-[6rem] w-[6rem] flex-nowrap rounded-2xl object-cover transition-opacity hover:opacity-80"
              />
              {product.out_of_stock && <SoldOverlay variant="sold" />}
            </div>
          </Link>

          <Link
            href={`/product/${product.slug}`}
            target="_blank"
            className="flex flex-col items-start gap-1"
          >
            <div className="flex items-center gap-2">
              <div className="h-[2.5rem] w-[6.4rem] rounded-xl bg-brand-secondary px-2 py-1 text-center text-[1.4rem] font-semibold text-white">
                OFFER
              </div>

              {offer && (
                <Chip
                  text={getOfferStatusBadge(offer, isOutgoingOffer)}
                  color={getChipColor(
                    offer
                      ? offerStateToText(offer.state).toLowerCase()
                      : 'pending'
                  )}
                  className="!h-[2.5rem] !rounded-xl !text-[1.5rem] !font-semibold"
                />
              )}
            </div>

            <div className="flex flex-col items-start gap-1">
              <h3 className="max-w-[35rem] truncate text-[1.8rem] font-semibold text-black">
                {product.title}
              </h3>

              <ProductPriceRange product={product} />

              {(selectedVariations.color || selectedVariations.size) && (
                <div className="flex items-center gap-4">
                  {selectedVariations.color && (
                    <span className="rounded-full bg-zinc-100 px-4 py-1 text-[1.6rem] font-medium text-zinc-600">
                      Selected Color: {selectedVariations.color}
                    </span>
                  )}

                  {selectedVariations.size && (
                    <span className="rounded-full bg-zinc-100 px-4 py-1 text-[1.6rem] font-medium text-zinc-600">
                      Selected Size: {selectedVariations.size}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Link>
        </div>
      );
    }

    if (order && (products?.length || product)) {
      if (isLoading) {
        return (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-start gap-6">
              <div className="flex flex-col items-start gap-1">
                <div className="h-[1.8rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[2.5rem] w-24 animate-pulse rounded bg-zinc-200" />
              </div>
            </div>

            <div className="duration-50 flex animate-pulse items-center justify-center gap-4 rounded-[1.5rem] bg-zinc-200 px-6 py-4" />
          </div>
        );
      }

      const orderStatusInfo = getOrderStatus(order, userDoc);

      return (
        <div className="flex w-full items-center justify-between">
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-start gap-1">
              <h3 className="text-[1.8rem] font-semibold text-black">
                Order #{order.order_num}
              </h3>

              {orderStatusInfo ? (
                <StatusBadgeNew
                  compact
                  status={orderStatusInfo.status}
                  is_partial={orderStatusInfo.is_partial}
                  variant={orderStatusInfo.variant}
                  date={orderStatusInfo.date}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <h4 className="text-[2.1rem] font-semibold text-zinc-700">
                    Order Status
                  </h4>
                  <span className="text-[1.8rem] font-semibold text-zinc-500">
                    Loading...
                  </span>
                </div>
              )}
            </div>
          </div>

          <Link
            href={`/dashboard/orders/${order.id}`}
            target="_blank"
            className="duration-50 text-zinc-black flex items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 px-6 py-4 text-[1.6rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
          >
            View Order
          </Link>
        </div>
      );
    }

    if (product) {
      if (isLoading) {
        return (
          <div className="flex w-full items-center justify-between">
            <div className="start flex gap-6">
              <div className="aspect-square h-[6rem] w-[6rem] animate-pulse rounded-2xl bg-zinc-200" />

              <div className="flex flex-col gap-1">
                <div className="h-[1.8rem] w-[30rem] animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.5rem] w-24 animate-pulse rounded bg-zinc-200" />
              </div>
            </div>

            <div className="h-[3.5rem] w-20 animate-pulse rounded-xl bg-zinc-200" />
          </div>
        );
      }

      return (
        <div className="flex w-full items-center justify-between gap-4">
          <Link
            href={`/product/${product.slug}`}
            target="_blank"
            className="start flex min-w-0 gap-6"
          >
            <div className="relative">
              <SafeImage
                src={product.thumbnail}
                alt={product.title ?? ''}
                width={400}
                height={400}
                className="aspect-square h-[6rem] w-[6rem] flex-nowrap rounded-2xl object-cover"
              />
              {product.out_of_stock && <SoldOverlay variant="sold" />}
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <h3 className="max-w-[30rem] truncate text-[1.8rem] font-semibold text-black">
                {product.title}
              </h3>

              <ProductPriceRange
                product={product}
                className="!text-[1.5rem] !font-semibold !text-zinc-500"
              />
            </div>
          </Link>

          <div className="flex shrink-0 items-center gap-3">
            {((product.size && (product.size.letter || product.size.number)) ||
              !!product.variations?.length) && (
              <div className="rounded-xl bg-zinc-200 px-4 py-2">
                <ProductCategoryDetails
                  product={product}
                  className="!text-[1.5rem]"
                />
              </div>
            )}

            {action}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="-mt-4 w-full border-y border-zinc-100 p-6">
      <PreviewContent />
    </div>
  );
}
